import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Linkedin, Facebook, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { ArticleCard } from "@/pages/Knowledge";
import { fmtArticleDate } from "@/lib/knowledge";

const WA_PATH = "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z";

export default function ArticlePage() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setA(null); setErr(false);
    api.get(`/knowledge/${slug}`).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-6 py-24">Article not found.</div>;
  if (!a) return <div className="max-w-3xl mx-auto px-6 py-24 text-neutral-500">Loading…</div>;

  const url = window.location.href;
  const shareUrl = `${API}/share/knowledge/${a.slug}`;
  const ogImage = a.featured_image
    ? (a.featured_image.startsWith("http") ? a.featured_image : window.location.origin + a.featured_image)
    : undefined;
  const enc = encodeURIComponent;

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo
        title={a.seo_title || `${a.title} — Transplant Story`}
        description={a.seo_description || a.description}
        image={ogImage}
        type="article"
        url={url}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.description || undefined,
          image: ogImage ? [ogImage] : undefined,
          author: { "@type": "Organization", name: a.author || "Transplant Story" },
          publisher: { "@type": "Organization", name: "Transplant Story", logo: { "@type": "ImageObject", url: `${window.location.origin}/assets/logo-full.png` } },
          datePublished: a.published_date || undefined,
          dateModified: a.updated_at || undefined,
          mainEntityOfPage: url,
        }}
      />

      <div className="text-[11px] font-mono uppercase tracking-widest text-rust mb-4">{a.category}</div>
      <h1 className="text-3xl sm:text-5xl font-light tracking-tight text-ink leading-tight">{a.title}</h1>
      {a.description && <p className="text-lg text-neutral-600 mt-5 leading-relaxed">{a.description}</p>}
      <div className="flex flex-wrap items-center gap-3 mt-5 text-[11px] font-mono text-neutral-400">
        {a.author && <span>{a.author}</span>}
        <span>·</span>
        <span>{a.reading_time} min read</span>
        {a.published_date && <><span>·</span><span>{fmtArticleDate(a.published_date)}</span></>}
      </div>

      {a.featured_image && (
        <img src={a.featured_image} alt={a.title} className="w-full aspect-[3/2] object-cover mt-10 border border-neutral-200" />
      )}

      <div
        data-testid="article-content"
        className="prose prose-neutral max-w-none mt-12 text-neutral-800 leading-relaxed [&_img]:max-w-full"
        dangerouslySetInnerHTML={{ __html: a.content || "" }}
      />

      <div className="mt-14 pt-8 border-t border-neutral-200 space-y-4">
        {a.sources && (
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-neutral-400 mb-1">Sources</div>
            <p className="text-sm text-neutral-600 whitespace-pre-wrap">{a.sources}</p>
          </div>
        )}
        <div className="font-mono text-[11px] uppercase tracking-widest text-neutral-400">
          Last updated · {fmtArticleDate(a.updated_at || a.published_date)}
        </div>
        <p className="text-xs text-neutral-400 leading-relaxed">
          This article is for general education only and is not medical advice, a clinical assessment, or a
          recommendation of any hospital or doctor. Always consult your transplant team for decisions about your care.
        </p>
      </div>

      {a.related && a.related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-normal tracking-tight text-ink mb-6">Related articles</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {a.related.map((r) => <ArticleCard key={r.id} a={r} />)}
          </div>
        </section>
      )}

      <div className="mt-14 pt-8 border-t border-neutral-200">
        <div className="text-[11px] font-mono uppercase tracking-widest text-neutral-400 mb-4">Share this article</div>
        <div className="flex flex-wrap gap-2">
          <ShareBtn testId="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`}>
            <Linkedin className="w-4 h-4" /> LinkedIn
          </ShareBtn>
          <ShareBtn testId="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`}>
            <Facebook className="w-4 h-4" /> Facebook
          </ShareBtn>
          <ShareBtn testId="share-whatsapp" href={`https://wa.me/?text=${enc(`${a.title} — ${shareUrl}`)}`}>
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true"><path d={WA_PATH} /></svg> WhatsApp
          </ShareBtn>
          <button
            data-testid="share-copy"
            onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }}
            className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:border-ink hover:text-ink transition-colors"
          >
            <Copy className="w-4 h-4" /> Copy Link
          </button>
        </div>
      </div>
    </article>
  );
}

function ShareBtn({ href, children, testId }) {
  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:border-ink hover:text-ink transition-colors"
    >
      {children}
    </a>
  );
}
